/* eslint no-use-before-define: ["error", { functions: false }] */
import {
  MaskOptions,
  InputState,
  Selection,
  ProcessChangeResult,
  FormatChars,
} from '../types';

import { findLastIndex, repeat } from './helpers';
import parseMask from './parse-mask';

export default class MaskUtils {
  private maskOptions: MaskOptions | null;

  constructor(options: {
    mask?: string | (string | RegExp)[];
    maskPlaceholder?: string | null;
    formatChars?: FormatChars;
  }) {
    this.maskOptions = parseMask(options);
  }

  isCharacterAllowedAtPosition = (
    character: string,
    position: number,
  ): boolean => {
    const options = this.maskOptions;
    if (!options) {
      return false;
    }

    const { maskPlaceholder } = options;

    if (this.isCharacterFillingPosition(character, position)) {
      return true;
    }

    if (!maskPlaceholder) {
      return false;
    }

    return maskPlaceholder[position] === character;
  };

  isCharacterFillingPosition = (
    character: string,
    position: number,
  ): boolean => {
    const options = this.maskOptions;
    if (!options) {
      return false;
    }

    const { mask } = options;

    if (!character || position >= mask.length) {
      return false;
    }

    const item = mask[position];

    return item.editable ? item.rule.test(character) : item.char === character;
  };

  isPositionEditable = (position: number): boolean => {
    const options = this.maskOptions;
    if (!options) {
      return false;
    }

    const item = options.mask[position];

    return item !== undefined && item.editable;
  };

  isValueEmpty = (value: string): boolean => {
    return value.split('').every((character, position) => {
      return (
        !this.isPositionEditable(position) ||
        !this.isCharacterFillingPosition(character, position)
      );
    });
  };

  isValueFilled = (value: string): boolean => {
    const options = this.maskOptions;
    if (!options) {
      return false;
    }

    return this.getFilledLength(value) === options.lastEditablePosition + 1;
  };

  getDefaultSelectionForValue = (value: string): Selection => {
    const filledLength = this.getFilledLength(value);
    const cursorPosition = this.getRightEditablePosition(filledLength);
    return { start: cursorPosition ?? 0, end: cursorPosition ?? 0 };
  };

  getFilledLength = (value: string): number => {
    const characters = value.split('');
    const lastFilledIndex = findLastIndex(characters, (character, position) => {
      return (
        this.isPositionEditable(position) &&
        this.isCharacterFillingPosition(character, position)
      );
    });
    return lastFilledIndex + 1;
  };

  getStringFillingLengthAtPosition = (
    string: string,
    position: number,
  ): number => {
    const characters = string.split('');
    const insertedValue = characters.reduce(
      (value, character) => {
        return this.insertCharacterAtPosition(value, character, value.length);
      },
      repeat(' ', position),
    );

    return insertedValue.length - position;
  };

  getLeftEditablePosition = (position: number): number | null => {
    for (let i = position; i >= 0; i--) {
      if (this.isPositionEditable(i)) {
        return i;
      }
    }
    return null;
  };

  getRightEditablePosition = (position: number): number | null => {
    const options = this.maskOptions;
    if (!options) {
      return null;
    }

    const { mask } = options;

    for (let i = position; i < mask.length; i++) {
      if (this.isPositionEditable(i)) {
        return i;
      }
    }

    return null;
  };

  formatValue = (value: string): string => {
    const options = this.maskOptions;
    if (!options) {
      return value;
    }

    const { maskPlaceholder, mask, literals } = options;

    if (!maskPlaceholder) {
      value = this.insertStringAtPosition('', value, 0);

      while (
        value.length < mask.length &&
        !this.isPositionEditable(value.length)
      ) {
        value += literals[value.length];
      }

      return value;
    }

    return this.insertStringAtPosition(maskPlaceholder, value, 0);
  };

  clearRange = (value: string, start: number, len: number): string => {
    if (!len) {
      return value;
    }

    const end = start + len;
    const options = this.maskOptions;

    if (!options) {
      return value;
    }

    const { maskPlaceholder, literals } = options;

    const clearedValue = value
      .split('')
      .map((character, i) => {
        const isEditable = this.isPositionEditable(i);

        if (!maskPlaceholder && i >= end && !isEditable) {
          return '';
        }
        if (i < start || i >= end) {
          return character;
        }
        if (!isEditable) {
          return literals[i];
        }
        if (maskPlaceholder) {
          return maskPlaceholder[i];
        }
        return '';
      })
      .join('');

    return this.formatValue(clearedValue);
  };

  insertCharacterAtPosition = (
    value: string,
    character: string,
    position: number,
  ): string => {
    const options = this.maskOptions;
    if (!options) {
      return value;
    }

    const { mask, maskPlaceholder, literals } = options;
    if (position >= mask.length) {
      return value;
    }

    const isAllowed = this.isCharacterAllowedAtPosition(character, position);
    const isEditable = this.isPositionEditable(position);
    const nextEditablePosition = this.getRightEditablePosition(position);
    const isNextPlaceholder =
      maskPlaceholder && nextEditablePosition
        ? character === maskPlaceholder[nextEditablePosition]
        : null;
    const valueBefore = value.slice(0, position);

    if (isAllowed || !isEditable) {
      const insertedCharacter = isAllowed ? character : literals[position];
      value = valueBefore + insertedCharacter;
    }

    if (!isAllowed && !isEditable && !isNextPlaceholder) {
      value = this.insertCharacterAtPosition(value, character, position + 1);
    }

    return value;
  };

  insertStringAtPosition = (
    value: string,
    string: string,
    position: number,
  ): string => {
    const options = this.maskOptions;
    if (!options) {
      return value;
    }

    const { mask, maskPlaceholder, literals } = options;
    if (!string || position >= mask.length) {
      return value;
    }

    const characters = string.split('');
    const isFixedLength = this.isValueFilled(value) || !!maskPlaceholder;
    const valueAfter = value.slice(position);

    value = characters.reduce(
      (val, character) => {
        return this.insertCharacterAtPosition(val, character, val.length);
      },
      value.slice(0, position),
    );

    if (isFixedLength) {
      value += valueAfter.slice(value.length - position);
    } else if (this.isValueFilled(value)) {
      value += literals.slice(value.length).join('');
    } else {
      const editableCharactersAfter = valueAfter
        .split('')
        .filter((character, i) => {
          return this.isPositionEditable(position + i);
        });
      value = editableCharactersAfter.reduce((val, character) => {
        const nextEditablePosition = this.getRightEditablePosition(val.length);
        if (nextEditablePosition === null) {
          return val;
        }

        if (!this.isPositionEditable(val.length)) {
          val += literals.slice(val.length, nextEditablePosition).join('');
        }

        return this.insertCharacterAtPosition(val, character, val.length);
      }, value);
    }

    return value;
  };

  processChange = (
    currentState: InputState,
    previousState: InputState,
  ): ProcessChangeResult => {
    const options = this.maskOptions;
    const { value, selection } = currentState;
    const previousValue = previousState.value;
    const previousSelection = previousState.selection;
    let newValue = value;
    let enteredString = '';
    let formattedEnteredStringLength = 0;
    let removedLength = 0;
    const previousStart = previousSelection.start ?? 0;
    const selectionStart = selection.start ?? 0;
    const selectionEnd = selection.end ?? 0;
    let cursorPosition = Math.min(previousStart, selectionStart);

    if (selectionEnd > previousStart) {
      enteredString = newValue.slice(previousStart, selectionEnd);
      formattedEnteredStringLength = this.getStringFillingLengthAtPosition(
        enteredString,
        cursorPosition,
      );
      if (!formattedEnteredStringLength) {
        removedLength = 0;
      } else {
        removedLength = previousSelection.length ?? 0;
      }
    } else if (newValue.length < previousValue.length) {
      removedLength = previousValue.length - newValue.length;
    }

    if (!options) {
      return {
        value: previousValue,
        enteredString,
        selection: { start: cursorPosition, end: cursorPosition },
      };
    }

    const { mask, prefix, lastEditablePosition } = options;

    newValue = previousValue;

    if (removedLength) {
      if (removedLength === 1 && !(previousSelection.length ?? 0)) {
        const deleteFromRight = previousStart === selectionStart;
        cursorPosition = deleteFromRight
          ? (this.getRightEditablePosition(selectionStart) ?? 0)
          : (this.getLeftEditablePosition(selectionStart) ?? 0);
      }
      newValue = this.clearRange(newValue, cursorPosition, removedLength);
    }

    newValue = this.insertStringAtPosition(
      newValue,
      enteredString,
      cursorPosition,
    );

    cursorPosition += formattedEnteredStringLength;
    if (cursorPosition >= mask.length) {
      cursorPosition = mask.length;
    } else if (
      prefix &&
      cursorPosition < prefix.length &&
      !formattedEnteredStringLength
    ) {
      cursorPosition = prefix.length;
    } else if (
      cursorPosition >= prefix.length &&
      cursorPosition < lastEditablePosition &&
      formattedEnteredStringLength
    ) {
      cursorPosition =
        this.getRightEditablePosition(cursorPosition) ?? cursorPosition;
    }

    newValue = this.formatValue(newValue);

    return {
      value: newValue,
      enteredString,
      selection: { start: cursorPosition, end: cursorPosition },
    };
  };
}
